import { useEffect } from 'react';

import useEvent from './useEvent';

type EventKey = keyof WindowEventMap;

type Listener<T extends EventKey> = (this: Window, event: WindowEventMap[T]) => unknown;

type Options = { enabled?: boolean } & AddEventListenerOptions;

type OptionsInput = boolean | Options;

const useWindowEvent = <T extends EventKey>(
  type: T,
  listener: Listener<T>,
  options?: OptionsInput,
) => {
  const listenerInternal = useEvent(listener);

  const hasOptions = !!options && typeof options === 'object';

  const optionParams = {
    capture: hasOptions ? options.capture : options,
    enabled: hasOptions && typeof options.enabled === 'boolean' ? options.enabled : true,
    once: hasOptions ? options.once : undefined,
    passive: hasOptions ? options.passive : undefined,
    signal: hasOptions ? options.signal : undefined,
  } satisfies Options;

  useEffect(() => {
    if (!optionParams.enabled) return undefined;

    const listenerOptions = {
      capture: optionParams.capture,
      once: optionParams.once,
      passive: optionParams.passive,
      signal: optionParams.signal,
    };

    window.addEventListener(type, listenerInternal, listenerOptions);

    return () => window.removeEventListener(type, listenerInternal, listenerOptions);
  }, [
    listenerInternal,
    optionParams.capture,
    optionParams.enabled,
    optionParams.once,
    optionParams.passive,
    optionParams.signal,
    type,
  ]);
};

export default useWindowEvent;
